import React from 'react';
import { PasswordInput as MantinePasswordInput, PasswordInputProps } from '@mantine/core';
import { toMantineError } from '../../theme/forms';
import { describedByContainer } from '../../theme/describedByContainer';
import { useFieldA11y } from '../../hooks/useFieldA11y';

export interface IInputPasswordProps extends Omit<PasswordInputProps, 'error' | 'size' | 'value'> {
    /** Ссылка на input-элемент */
    ref?: React.Ref<HTMLInputElement>;
    /** Размер по шкале дизайн-системы @default 'sm' */
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    /** Контролируемое значение; null из форм нормализуется в пустую строку. */
    value?: PasswordInputProps['value'] | null;
    /**
     * Невалидное состояние (красная рамка). Переходный boolean-контракт: его
     * инжектит FormControl.cloneElement, текст ошибки рендерит FormGroup. Полный
     * переход на error: ReactNode — вместе с переписыванием FormControl
     * (см. API-GUIDELINES §5).
     */
    invalid?: boolean;
    /**
     * Отклонение от Mantine onChange(event): отдаёт нормализованное строковое
     * значение для InputPasswordControl → RHF (см. API-GUIDELINES §6).
     */
    onValueChange?: (value: string) => void;
    /** Показывать кнопку-тоггл видимости пароля @default true */
    withVisibilityToggle?: boolean;
}

// Канонический образец атома-фасада (см. API-GUIDELINES): узкий тип на Mantine-props
// (без наследования легаси IInputProps), data-testid пробрасывается as-is, явная
// size-шкала, invalid→error через мост toMantineError, документированные отклонения
// (withVisibilityToggle, onValueChange).
const InputPassword: React.FC<IInputPasswordProps> = ({
    ref,
    invalid,
    onValueChange,
    onChange,
    withVisibilityToggle = true,
    size = 'sm',
    styles,
    value,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    visibilityToggleButtonProps,
    inputContainer,
    ...rest
}) => {
    const fieldA11y = useFieldA11y(
        { 'aria-label': rest['aria-label'], 'aria-labelledby': ariaLabelledBy, 'aria-describedby': ariaDescribedBy },
        !!rest.label,
    );
    return (
        <MantinePasswordInput
            {...rest}
            aria-labelledby={fieldA11y['aria-labelledby']}
            inputContainer={describedByContainer(fieldA11y['aria-describedby'], inputContainer)}
            // aria-invalid поля пароля Mantine не ставит (у обёртки withAria выключен)
            aria-invalid={invalid || undefined}
            visibilityToggleButtonProps={{ 'aria-label': 'Показать пароль', ...visibilityToggleButtonProps }}
            ref={ref}
            // null из форм → '': иначе React считает поле неконтролируемым (предупреждение
            // «value prop should not be null») и не чистит DOM при сбросе значения в null.
            value={value === null ? '' : value}
            size={size}
            error={toMantineError(invalid)}
            // Mantine 9 не имеет withVisibilityToggle — прячем тогл через Styles API слот.
            // МЕРЖИМ с потребительскими styles (а не перезаписываем): иначе при
            // withVisibilityToggle=false потребительские слоты (input/wrapper/…) терялись бы.
            styles={withVisibilityToggle ? styles : { ...styles, visibilityToggle: { display: 'none' } }}
            onChange={(e) => {
                onChange?.(e);
                onValueChange?.(e.currentTarget.value);
            }}
        />
    );
};

export default InputPassword;
